import { Injectable } from '@angular/core';
import { Observable, finalize, shareReplay, tap, throwError, timeout } from 'rxjs';

/** Sent to the host when this frame's token is no longer accepted. Carries no data. */
export const MSG_TOKEN_EXPIRED = 'nubo:widget-token-expired';

/** The host's reply, carrying a freshly issued token. */
export const MSG_WIDGET_TOKEN = 'nubo:widget-token';

const TOKEN_KEY = 'widget-token';

/** Long enough for the host's round trip to Nubo.Api, short enough not to hang a message send. */
const RENEWAL_TIMEOUT_MS = 10000;

/**
 * Obtains a replacement widget token without reloading anything.
 *
 * The frame cannot mint its own: tokens are issued to the *embedding* origin, and a request from
 * here reports the chat domain instead. Only the host page can ask. Earlier this was handled by
 * having the host rebuild the frame, which renewed the token and destroyed the conversation with
 * it — the transcript lives only in component memory, while the session id and the model's context
 * survive, so the visitor was left with an empty panel from an assistant that still remembered
 * them. Renewing in place means nothing unloads and nothing can be lost.
 */
@Injectable({ providedIn: 'root' })
export class WidgetTokenService {
  /** Shared while a renewal is in flight, so several failing requests ask the host once. */
  private inFlight: Observable<string> | null = null;

  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  store(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  renew(): Observable<string> {
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.askHost().pipe(
      tap(token => this.store(token)),
      // Before shareReplay so it runs once when the request settles, rather than per subscriber.
      finalize(() => { this.inFlight = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.inFlight;
  }

  private askHost(): Observable<string> {
    if (window.parent === window) {
      // Not embedded — the operator-portal preview renders this as a custom element. There is no
      // host to ask, and no token flow to recover.
      return throwError(() => new Error('Not embedded in a host page; cannot renew the widget token.'));
    }

    return new Observable<string>(subscriber => {
      const onMessage = (event: MessageEvent): void => {
        // Only the window we are embedded in may answer. Origin cannot be checked here — which
        // origin the host is, is precisely what this frame is unable to observe, and is the reason
        // the token exists at all. A hostile parent gains nothing by replying: it cannot obtain a
        // valid token from /Configs/load, and anything else it sends is refused by Chat.Api.
        if (event.source !== window.parent) return;

        const data = event.data;
        if (!data || typeof data !== 'object' || data.type !== MSG_WIDGET_TOKEN) return;

        const token = typeof data.token === 'string' ? data.token.trim() : '';
        if (!token) {
          subscriber.error(new Error('Host replied without a token.'));
          return;
        }

        subscriber.next(token);
        subscriber.complete();
      };

      window.addEventListener('message', onMessage);

      // The stale token is dropped first, so nothing retries with it while the host is answering.
      this.clear();
      // No data, so '*' leaks nothing. The reply is the half that carries a credential, and the
      // host addresses that one to this frame specifically.
      window.parent.postMessage({ type: MSG_TOKEN_EXPIRED }, '*');

      return () => window.removeEventListener('message', onMessage);
    }).pipe(timeout(RENEWAL_TIMEOUT_MS));
  }
}
